import {useNavigate} from "react-router-dom";
import {store} from "../../../redux/store";
import {useProfileStatus} from "../../../features/profile-status/hooks";

/**
 * Single source of truth for "where does a coach go after auth is fully
 * resolved" (2FA is no longer pending). Used both right after a normal login
 * and after the user finishes/dismisses the 2FA nudge, so KYC/redirectTo
 * logic isn't duplicated across those two entry points.
 *
 * KYC status comes from useProfileStatus(), not the legacy redux
 * `profileData.isKyc_verified` — the v2 /profile endpoint never populates
 * that field (see profile.mapper.v2.ts), so it was always false/undefined.
 *
 * Pass `enabled: false` when this hook is mounted before login has actually
 * succeeded (e.g. LoginForm renders it unconditionally on the login page) so
 * it doesn't fire an authenticated request before there's anything to
 * authenticate with.
 */
export const useResolvePostAuthRedirect = (enabled: boolean = true) => {
  const navigate = useNavigate();
  const profileStatusQuery = useProfileStatus(enabled);

  const resolvePostAuthRedirect = (redirectTo?: string | null) => {
    const state = store.getState();
    const isCoach = !state.userLogin.userInfo?.isCustomer;
    // Only an actual answer counts as "not verified". If the status call failed
    // we don't know, and diverting a verified coach away from their intended
    // redirectTo is the worse of the two mistakes — the dashboard prompts for
    // KYC on its own anyway.
    const isKnownUnverified = profileStatusQuery.data?.kycVerified === false;

    if (isCoach && isKnownUnverified) {
      navigate(`/home`);
    } else if (redirectTo) {
      navigate(`${redirectTo}`);
    } else {
      navigate(`/onboarding`);
    }
  };

  /**
   * True until the status query has actually produced an answer.
   *
   * Deliberately not `isFetching`: a query whose `enabled` has just flipped
   * true still reports `fetchStatus: "idle"` on that render, because the
   * request only starts in a later effect. Callers gating a redirect on
   * `!isFetching` therefore fire one render too early, read `data` as
   * undefined, and bounce every coach to /home. `isFetched` stays false
   * until the query settles, and — unlike waiting on `data` — flips true on
   * error too, so a failed status call can't hang the redirect forever.
   */
  const isKycStatusLoading = !profileStatusQuery.isFetched;

  return {resolvePostAuthRedirect, isKycStatusLoading};
};
